import React, { useContext, useState } from "react"
import {
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
} from "@material-ui/core"
import PropTypes from "prop-types"
import GetAppIcon from "@material-ui/icons/GetApp"
import SettingsIcon from "@material-ui/icons/Settings"
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline"
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline"
import { MessagingContext } from "../../contexts/MessagingContext"
import { getGetSignedUrl, fetchFile } from "../../api/apiCalls"
import { UserContext } from "../../contexts/UserContext"
import { HistoryContext } from "../../contexts/HistoryContext"
import { makeStyles } from "@material-ui/core/styles"
import { red } from "@material-ui/core/colors"
import { green } from "@material-ui/core/colors"
import { grey } from "@material-ui/core/colors"

const useStyles = makeStyles(() => ({
  notificationSuccess: {
    color: green[500],
  },
  notificationError: {
    color: red[500],
  },
  notificationMessage: {
    color: grey[500],
  },
}))

const formatItem = (item) => {
  const timestamp = `${new Date(
    item.createdAt.replace(" ", "T")
  ).toLocaleString("en-US", { timeZone: "America/New_York" })} ET`

  const initialMessage = `${timestamp} - ${item.user ? item.user : ""}`

  let detail

  switch (item.activity_type_id) {
    case 13:
      detail = `Megafile Failed : ${item.message}`
      break

    case 12:
      detail = `requested new megafile`
      break

    case 11:
      detail = item.message || `error uploading template ${item.filename}`
      break

    case 10:
      detail = `error downloading template ${item.filename}`
      break

    case 9:
      detail = `error downloading report ${item.filename}`
      break

    case 8:
      detail = `downloaded template ${item.filename}`
      break

    case 7:
      detail = `downloaded report ${item.filename}`
      break

    case 6:
      detail = `requested report ${item.filename}`
      break

    case 5:
      detail = `loaded ${item.context}`
      break

    case 4:
      detail = `invalidated ${item.context} schema`
      break

    case 3:
      if (item.context === "megafile") {
        detail = `${item.context} refreshed`
      } else {
        detail = `successfully loaded as ${item.context}`
      }
      break

    case 2:
      detail = `downloaded ${item.filename}`
      break

    case 1:
      detail = item.message || `uploaded ${item.filename} to ${item.context}`
      break

    default:
      detail = `executed action`
  }

  return `${initialMessage} ${detail}`
}

const HistoryListItem = ({ item }) => {
  const classes = useStyles()
  const { activity_type_id, downloadable } = item
  const isButton = activity_type_id === 6

  const { addSuccessMessage, addErrorMessage } = useContext(MessagingContext)
  const [transferring, setTransferring] = useState(false)
  const [downloaded, setDownloaded] = useState(false)
  const [hasError, setHasError] = useState(false)

  const { user } = useContext(UserContext)
  const { fetchHistory, saveActivity, saveError } = useContext(HistoryContext)

  const progressCallback = (progress) => {
    console.log("progress", progress)
  }

  const completionCallback = async () => {
    addSuccessMessage("DOWNLOAD", `${item.filename} Download Complete`)
    setTransferring(false)
    setDownloaded(true)
    await saveActivity(user, item.filename, false, item.context)
    fetchHistory(user)
  }

  const errorCallback = async (error) => {
    console.log(error)
    setTransferring(false)
    addErrorMessage("DOWNLOAD ERROR", `${item.filename} Not Found`)
    setHasError(true)
    setDownloaded(true)
    await saveError(user, item.filename, true, item.context)
    fetchHistory(user)
  }

  const downloadFile = () => {
    if (transferring) {
      return
    }
    setTransferring(true)
    getGetSignedUrl(user, "downloads", item.context, item.filename).then(
      ({ url }) => {
        fetchFile(
          url,
          item.filename,
          progressCallback,
          completionCallback,
          errorCallback
        )
      }
    )
  }

  const getButtonStatus = () => {
    if (hasError) {
      return "ERROR - FILE NOT FOUND"
    }
    if (downloaded) {
      return "DOWNLOADED"
    }
    if (transferring) {
      return "DOWNLOADING"
    }
    return downloadable ? "READY TO DOWNLOAD" : "PROCESSING"
  }

  const getCopyClass = (item) => {
    let str = item.message || ""
    if (
      str.startsWith("INFO:  200") ||
      item.activity_type_id == 1 ||
      item.activity_type_id == 2 ||
      item.activity_type_id == 3 ||
      item.activity_type_id == 7
    ) {
      return classes.notificationSuccess
    } else if (
      str.startsWith("INFO:  -1") ||
      str.startsWith('{"errorMessage"') ||
      str.startsWith("error") ||
      item.activity_type_id == 9 ||
      item.activity_type_id == 10 ||
      item.activity_type_id == 11 ||
      item.activity_type_id == 13
    ) {
      return classes.notificationError
    } else {
      return classes.notificationMessage
    }
  }

  const getIcon = (item) => {
    let str = item.message || ""
    if (
      str.startsWith("INFO:  -1") ||
      str.startsWith('{"errorMessage"') ||
      str.startsWith("error") ||
      item.activity_type_id == 9 ||
      item.activity_type_id == 10 ||
      item.activity_type_id == 11 ||
      item.activity_type_id == 13
    ) {
      return <ErrorOutlineIcon style={{ color: red[500] }} />
    }
    if (
      str.startsWith("INFO:  200") ||
      item.activity_type_id == 1 ||
      item.activity_type_id == 2 ||
      item.activity_type_id == 3 ||
      item.activity_type_id == 7
    ) {
      return <CheckCircleOutlineIcon style={{ color: green[500] }} />
    }
    if (downloadable) {
      if (transferring) {
        return <CircularProgress size={24} color="secondary" />
      } else {
        return <GetAppIcon color="secondary" />
      }
    } else {
      return <SettingsIcon color="primary" />
    }
  }

  return (
    <ListItem
      onClick={() => {
        if (downloadable) {
          downloadFile()
        }
      }}
      button={isButton}
      disabled={isButton && !downloadable}
      key={item.id}
    >
      <ListItemIcon>{getIcon(item)}</ListItemIcon>
      <ListItemText
        disableTypography
        primary={<div className={getCopyClass(item)}>{formatItem(item)}</div>}
        secondary={isButton && getButtonStatus()}
      />
    </ListItem>
  )
}

HistoryListItem.propTypes = {
  item: PropTypes.object,
}

export default HistoryListItem
